import { useEffect, useRef } from 'react';

import { useWorkspace } from '@/hooks/useWorkspace';
import { useLocale } from '@/i18n/LocaleProvider';
import {
  TENANT_LOCALE_STORAGE_KEY,
  normalizeLocale,
} from '@/i18n/config';

// Reconciles the active UI locale with the tenant's configured
// `default_language` on first load for authenticated users.
//
// The tenant's `default_language` is the single source of truth for the
// authenticated UI. On every fresh mount (i.e. each login) we adopt it once,
// overriding whatever transient locale the public landing / login pages left
// behind in localStorage. This is what stops one tenant's language choice
// from leaking into a *different* tenant signed in later on the same browser:
// when a user changes the language via the globe, that choice is persisted
// onto the tenant's `default_language` (see LanguageSwitchConfirmDialog), so
// re-adopting the tenant default here always reflects the user's own choice
// for *this* tenant, never a stale value carried over from the login page.
//
// Landing-page -> tenant continuity for the *signup* and *invitation* flows is
// handled upstream: those flows persist the chosen locale onto the new
// tenant's `default_language` at creation time, so adopting the tenant default
// here yields the same result without the cross-tenant leak. Plain login never
// carries the login-page language into the tenant.
//
// `hasSynced` guards against re-applying the default on background refetches
// (e.g. right after a globe switch optimistically updates the workspace),
// which would otherwise revert an in-session language change.
export function TenantLocaleSync() {
  const { data: workspace } = useWorkspace();
  const { setLocale } = useLocale();
  // Track whether we have already synced on this mount so we do not apply
  // the workspace default again on every background refetch.
  const hasSynced = useRef(false);

  useEffect(() => {
    if (!workspace?.default_language) return;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(
        TENANT_LOCALE_STORAGE_KEY,
        normalizeLocale(workspace.default_language),
      );
    }
  }, [workspace?.default_language]);

  useEffect(() => {
    if (hasSynced.current) return;
    if (!workspace?.default_language) return;

    hasSynced.current = true;
    void setLocale(normalizeLocale(workspace.default_language));
  }, [workspace?.default_language, setLocale]);

  return null;
}
